import React, { useState, useRef, useEffect } from 'react';
import { Printer, FileSpreadsheet, FileText, ChevronDown, Loader2, X } from "lucide-react";
import { pdf } from '@react-pdf/renderer';
import * as XLSX from 'xlsx';

const ExportButton = ({ 
  // Function that returns the PDF component with data
  getPDFComponent,
  // Optional custom filename without extension
  filename = 'document',
  // Optional function to fetch additional data before generating PDF
  fetchData,
  // Optional custom error handler
  onError,
  // Optional custom classes for the button
  className = "text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors duration-300",
  // Optional custom size for the icon
  iconSize = 20
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportAs = async (type) => {
    setExportType(type);
    setIsLoading(true);
    
    try {
      // If fetchData is provided, get the additional data first
      let data = {};
      if (fetchData) {
        data = await fetchData();
      }

      switch (type) {
        case 'pdf':
          await exportAsPDF(data);
          break;
        case 'excel':
          exportAsExcel(data);
          break;
        case 'csv':
          exportAsCSV(data);
          break;
        default:
          throw new Error('Invalid export type');
      }
    } catch (error) {
      console.error(`Error generating ${type}:`, error);
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
      setExportType(null);
      setIsMenuOpen(false);
    }
  };

  const exportAsPDF = async (data) => {
    // Get the PDF component with the data
    const PDFComponent = await getPDFComponent(data);
    
    // Generate the PDF blob
    const blob = await pdf(PDFComponent).toBlob();
    
    // Create and trigger download
    downloadFile(blob, `${filename}.pdf`, 'application/pdf');
  };

  const exportAsExcel = (data) => {
    // Format data for Excel
    const workbookData = formatDataForExport(data);
    
    // Generate Excel file
    const worksheet = XLSX.utils.json_to_sheet(workbookData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Indent");
    
    // Generate buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Download file
    downloadFile(blob, `${filename}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  };

  const exportAsCSV = (data) => {
    // Format data for CSV
    const csvData = formatDataForExport(data);
    
    // Convert to CSV
    const worksheet = XLSX.utils.json_to_sheet(csvData);
    const csvContent = XLSX.utils.sheet_to_csv(worksheet);
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `${filename}.csv`, 'text/csv');
  };

  const formatDataForExport = (data) => {
    // Format the indent data for Excel/CSV export
    const exportData = [];
    
    // Add indent header info
    exportData.push({
      "Indent ID": data.indentId,
      "Date": data.creationDate,
      "Project": data.projectName,
      "Task Scheduled": data.taskScheduled,
      "Status": data.status,
      "Type": data.invoicingType,
      "Created By": data.userName
    });
    
    // Add a blank row for separation
    exportData.push({});
    
    // Add column headers for items
    exportData.push({
      "Item Name": "Item Name",
      "Category": "Category",
      "Unit": "Unit",
      "Quantity": "Quantity",
      "Received": "Received",
      "Difference": "Difference",
      "Stock": "Stock",
      "Description": "Description",
      "Promised (Normal)": "Promised Days",
      "Promised (Emergency)": "Emergency Days",
      "Committed Date": "Committed Date",
      "Remarks": "Remarks"
    });
    
    // Add item rows
    data.items.forEach(item => {
      exportData.push({
        "Item Name": item.name,
        "Category": item.category,
        "Unit": item.au,
        "Quantity": item.quantity,
        "Received": item.quantityReceived,
        "Difference": item.difference,
        "Stock": item.stock,
        "Description": item.description,
        "Promised (Normal)": item.promisedDays,
        "Promised (Emergency)": item.promisedDays2,
        "Committed Date": item.committedDate,
        "Remarks": item.remarks
      });
    });
    
    return exportData;
  };

  const downloadFile = (blob, fileName, fileType) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const renderLoadingIcon = () => (
    <Loader2 size={iconSize} className="animate-spin" />
  );

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        className={`${className} flex items-center`}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        disabled={isLoading}
        title="Export options"
      >
        {isLoading ? (
          renderLoadingIcon()
        ) : (
          <>
            <Printer size={iconSize} />
            <ChevronDown size={iconSize - 4} className="ml-1" />
          </>
        )}
      </button>
      
      {isMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
          <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200">
            <span className="text-sm font-medium">Export Options</span>
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
          <div className="py-1">
            <button
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
              onClick={() => handleExportAs('pdf')}
              disabled={isLoading}
            >
              {exportType === 'pdf' && isLoading ? (
                renderLoadingIcon()
              ) : (
                <>
                  <Printer size={iconSize - 2} className="mr-2" />
                  <span>Export as PDF</span>
                </>
              )}
            </button>
            <button
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
              onClick={() => handleExportAs('excel')}
              disabled={isLoading}
            >
              {exportType === 'excel' && isLoading ? (
                renderLoadingIcon()
              ) : (
                <>
                  <FileSpreadsheet size={iconSize - 2} className="mr-2" />
                  <span>Export as Excel</span>
                </>
              )}
            </button>
            <button
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
              onClick={() => handleExportAs('csv')}
              disabled={isLoading}
            >
              {exportType === 'csv' && isLoading ? (
                renderLoadingIcon()
              ) : (
                <>
                  <FileText size={iconSize - 2} className="mr-2" />
                  <span>Export as CSV</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportButton;